import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@crm-src/hooks/use-toast"
import { useResellers } from "@crm-src/hooks/useResellers"
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { PageLoader } from "@/components/ui/page-loader"
import { withProspectsAccess } from "@crm-src/components/page-permission-guard"
import {
  Edit,
  X,
  User,
  FileText,
  Activity as ActivityIcon,
  TrendingUp,
  MessageSquare,
  Contact,
  MoreHorizontal,
  Sparkles,
} from "lucide-react"

import { leadsApi as leadsApiService, isAiProspect } from "@crm-src/lib/leads-api"
import { prospectListPathForLead } from "../prospects-origin"
import type {
  DetailedLead,
  LeadPriority
} from "@crm-src/types/lead"
import { useFinancialAccess, useMoneyFormatter } from "@crm-src/components/money"
// Dashboard-side shared share components — relative imports (NOT `@/`, which the
// CRM tsconfig remaps to src/crm). See docs/crm.md + the share-to-chat spec.
import { buildEntityShareItems } from "../../../../components/share/entityShareItems"
import { ShareMenu } from "../../../../components/share/ShareMenu"
import { ShareToChatDialog } from "../../../../components/share/ShareToChatDialog"
import { api, ApiError } from "../../../../lib/api"
import { prospectShareSubject } from "../../../lib/shareSubject"
import type { SalesOrgLeadDetail } from "@xop/dashboard-shared"
import { AiLeadBriefModal } from "@crm-src/components/ai-lead-brief-modal"

// Import panel components
import { FileManagerPanel } from "@crm-src/components/file-manager-panel"
import { NotesPanel } from "@crm-src/components/notes-panel"
import { ActivityLogPanel } from "@crm-src/components/activity-log-panel"
import { ProspectFormModal } from "@crm-src/components/prospect-form-modal"
import { DeleteProspectModal } from "@crm-src/components/delete-prospect-modal"
import { EditableContactInformationPanel } from "@crm-src/components/editable-contact-information-panel"
import { DocumentList } from "@crm-src/components/document-list"
import type { DocumentItem } from "@crm-src/components/document-list"
import { useInvoices } from "@crm-src/hooks/useInvoices"
import { invoicesApi } from "@crm-src/lib/invoices-api"
import { DetailTabs } from "@crm-src/components/detail-tabs"
import { ProspectOverviewTab } from "@crm-src/components/prospect-overview-tab"
import { useTabParam } from "@crm-src/hooks/useTabParam"
import usePermissions from "@crm-src/hooks/usePermissions"
import { useCan } from "@/auth/permissions"

import type { DetailTab } from "@crm-src/components/detail-tabs"
import { BackButton } from "@/components/ui/back-button"




const PROSPECT_TABS: DetailTab[] = [
  // Mirrors the customer detail page: Overview shows info + contacts side
  // by side (no separate Contacts tab); Notes + Files share one tab.
  { id: "overview", label: "Overview", icon: User },
  { id: "activity", label: "Activity", icon: ActivityIcon },
  { id: "quotes", label: "Quotes", icon: FileText },
  { id: "notes", label: "Notes & Files", icon: MessageSquare },
]
const PROSPECT_TAB_IDS = PROSPECT_TABS.map((tab) => tab.id)

// ACTION tab, not a content tab — always LAST, AI-created prospects only.
// It never becomes the active tab (it's not in the useTabParam id list, so a
// `?tab=ai_lead` deep-link falls back to Overview); clicking it triggers the
// AI-lead resolution instead (super_admin → /sales/leads/:id, everyone else →
// the read-only brief modal). See docs/crm.md → "'AI Lead' action".
const AI_LEAD_TAB: DetailTab = { id: "ai_lead", label: "AI Lead", icon: Sparkles }

function ProspectDetailsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { isAdmin } = usePermissions()
  // WHO MANAGES A RECORD (CEO, 2026-09-16): "a salesperson should be capable
  // to do everything with an assigned customer". Management follows visibility
  // — every row on this page is one this user can see (crm-api narrows the list)
  // — so the controls follow `crm:edit`, not "is an administrator". crm-api
  // enforces the same rule (CustomerManagement); a read-only role such as
  // marketing_manager still gets no write controls.
  const canManage = useCan("crm:edit")
  const { resellers, isLoading: resellersLoading } = useResellers()
  const { getInvoices, removeInvoice, markAsPaid } = useInvoices()
  const prospectId = params.id as string
  // Financial wall: Quotes are priced documents, so the whole Quotes tab is
  // hidden for a viewer without financial access (crm:financials) — consistent
  // with the customer Billing tab. A direct `?tab=quotes` deep-link falls back
  // to Overview. See components/money.tsx.
  const hasFinancialAccess = useFinancialAccess()
  const visibleProspectTabs = hasFinancialAccess
    ? PROSPECT_TABS
    : PROSPECT_TABS.filter((tab) => tab.id !== "quotes")
  const visibleProspectTabIds = visibleProspectTabs.map((tab) => tab.id)
  const [activeTab, setActiveTab] = useTabParam(visibleProspectTabIds, "overview")
  const formatMoney = useMoneyFormatter()

  // State management
  const [prospect, setProspect] = useState<DetailedLead | null>(null)
  const [loading, setLoading] = useState(true)
  const [quotes, setQuotes] = useState<DocumentItem[]>([])
  const [quotesLoading, setQuotesLoading] = useState(false)

  // Actions menu anchor — replaces the shadcn DropdownMenu during the MUI 5
  // migration.
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null)

  // Modal states
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [chatShareOpen, setChatShareOpen] = useState(false)
  // "AI Lead" (AI-created prospects only): super_admins jump to the original
  // Sales lead page; everyone else gets the read-only brief modal — the Sales
  // section itself stays super_admin/sales:view territory. See docs/crm.md.
  const [aiLeadModalOpen, setAiLeadModalOpen] = useState(false)
  const [aiLeadResolving, setAiLeadResolving] = useState(false)

  // Note: the Activities tab on this page is currently waiting for the
  // CRM API to expose a `/leads/:id/activities` endpoint. Previously
  // this file shipped two hard-coded "demo" datasets that rendered into
  // the live UI — staff users saw fake John Smith / Sarah Johnson rows
  // for every prospect. Both datasets have been removed; until the API
  // ships, the tab should render nothing (or an explicit empty state)
  // rather than fictional data.

  // Load prospect details
  const loadProspect = async () => {
    try {
      setLoading(true)
      
      const prospectResponse = await leadsApiService.getById(prospectId)
      const lead = prospectResponse.lead
      
      setProspect(lead)
      
    } catch (error) {
      console.error('Error loading prospect details:', error)
      toast({
        title: "Error",
        description: "Failed to load prospect details",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // Load quotes for prospect
  const loadQuotes = async () => {
    try {
      setQuotesLoading(true)
      const response = await getInvoices({
        customer_id: prospectId,
        document_type: 'quote'
      })
      
      // Transform to DocumentItem format
      const transformedQuotes: DocumentItem[] = response.invoices.map((invoice: any) => ({
        id: invoice.id.toString(),
        number: invoice.invoice_number || invoice.number,
        total_amount: invoice.total_amount || invoice.total || 0,
        status: invoice.payment_status || invoice.status,
        date: invoice.issue_date || invoice.date,
        due_date: invoice.due_date,
        file_url: invoice.file_url,
        has_file: !!invoice.file_url,
        original_filename: invoice.original_filename,
        document_type: 'quote'
      }))
      
      setQuotes(transformedQuotes)
    } catch (error) {
      console.error('Error loading quotes:', error)
      toast({
        title: "Error",
        description: "Failed to load quotes",
        variant: "destructive",
      })
    } finally {
      setQuotesLoading(false)
    }
  }

  useEffect(() => {
    if (prospectId) loadProspect()
  }, [prospectId])

  // Financial wall: Quotes are priced documents. The Quotes tab is already
  // hidden for a viewer without `crm:financials` (see visibleProspectTabs), and
  // the FETCH must be skipped too — `loadQuotes` hits the financial `/invoices`
  // endpoint, which the wall 403s (at the dashboard proxy's FINANCIAL_V1_PATTERN
  // and crm-api), surfacing a spurious "Failed to load quotes" error on EVERY
  // prospect open for a non-financial role (e.g. marketing_manager). Only fetch
  // when the viewer can actually see money. `hasFinancialAccess` is in the deps
  // so a financial viewer still loads quotes once access resolves.
  useEffect(() => {
    if (prospectId && hasFinancialAccess) loadQuotes()
  }, [prospectId, hasFinancialAccess])

  // Inline edit (Overview tab): assign salesperson / change priority in place
  // without opening the edit modal. Both throw on failure so the shared
  // <InlineEditField> surfaces the error toast; on success we reload.
  // See docs/crm.md → "Inline editing".
  const handleInlineAssignSalespeople = async (userIds: string[]) => {
    await leadsApiService.bulkAssignSalespeople([prospectId], userIds)
    await loadProspect()
    toast({ title: "Saved", description: "Salesperson updated" })
  }

  const handleInlineChangePriority = async (value: LeadPriority) => {
    await leadsApiService.update(prospectId, { priority: value })
    await loadProspect()
    toast({ title: "Saved", description: "Priority updated" })
  }

  const handleInlineChangeIndustry = async (value: string) => {
    await leadsApiService.update(prospectId, { industry: value })
    await loadProspect()
    toast({ title: "Saved", description: "Industry updated" })
  }

  // Reseller assignment reuses the bulk endpoint (bulk-of-one); "none" === Direct.
  const handleInlineAssignReseller = async (resellerId: string) => {
    await leadsApiService.bulkAssignReseller([prospectId], resellerId)
    await loadProspect()
    toast({ title: "Saved", description: "Reseller updated" })
  }

  // Reseller options for the inline-edit select. "none" === Direct — the same
  // sentinel the bulk-assign-reseller modal sends.
  const resellerOptions = [
    { value: "none", label: "Direct" },
    ...resellers.map((r) => ({ value: String(r.id), label: r.name })),
  ]

  // Handle edit prospect
  const handleEditProspect = () => {
    setIsEditDialogOpen(true)
  }

  // Handle prospect update from modal
  const handleProspectUpdated = (updatedProspect: DetailedLead) => {
    setProspect(updatedProspect)
  }

  // Handle delete prospect
  const handleDeleteProspect = () => {
    setIsDeleteDialogOpen(true)
  }

  // "AI Lead": resolve the originating sales-agent lead for this AI-created
  // prospect. In the CRM SPA only a super_admin ever holds `admin:access`
  // (crmSessionPermissions.ts), so isAdmin() ⇔ super_admin here — they jump to
  // the live Sales lead page. Everyone else opens the read-only brief modal
  // (they can't reach /sales at all).
  const handleAiLead = async () => {
    if (!isAdmin()) {
      setAiLeadModalOpen(true)
      return
    }
    try {
      setAiLeadResolving(true)
      const lead = await api<SalesOrgLeadDetail>(
        `/api/sales/v2/leads/by-crm/${encodeURIComponent(prospectId)}`,
      )
      navigate(`/sales/leads/${encodeURIComponent(lead.id)}`)
    } catch (error) {
      const notFound = error instanceof ApiError && error.status === 404
      toast({
        title: notFound ? "AI lead not linked" : "Error",
        description: notFound
          ? "This prospect is no longer linked to an AI lead — it may have been released or removed."
          : "Failed to open the AI lead. Please try again later.",
        variant: "destructive",
      })
    } finally {
      setAiLeadResolving(false)
    }
  }

  // Handle navigation after successful deletion. Return to the list this
  // prospect belongs to (AI prospects → /crm/ai-prospects).
  const handleNavigateToList = () => {
    navigate(prospectListPathForLead(prospect))
  }

  // Handle convert to customer
  const handleConvertToCustomer = async () => {
    if (!prospect) return
    
    try {
      const result = await leadsApiService.convert(prospect.id)
      
      toast({
        title: "Success", 
        description: `${prospect.full_name} converted successfully! Created customer contact.`,
      })
      
      // Navigate back to the list this prospect came from.
      navigate(prospectListPathForLead(prospect))

    } catch (error: any) {
      console.error('Error converting to customer:', error)
      
      const errorData = error?.response?.data
      const errorMessage = errorData?.error || 'Failed to convert to customer'
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  // Handle delete quote
  const handleDeleteQuote = async (quoteId: string) => {
    try {
      await removeInvoice(parseInt(quoteId))
      toast({
        title: "Success",
        description: "Quote deleted successfully",
      })
      await loadQuotes()
    } catch (error) {
      console.error('Error deleting quote:', error)
      toast({
        title: "Error",
        description: "Failed to delete quote",
        variant: "destructive",
      })
    }
  }

  // Handle download quote
  const handleDownloadQuote = async (document: DocumentItem) => {
    try {
      if (!document.has_file) {
        toast({
          title: "Error",
          description: "Quote file not available",
          variant: "destructive",
        })
        return
      }
      
      // Use invoicesApi to download the file with proper authentication
      const result = await invoicesApi.downloadFile(document.id, document.original_filename)
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to download file')
      }
      
      toast({
        title: "Success",
        description: "Quote file downloaded successfully",
      })
    } catch (error: any) {
      console.error('Error downloading quote:', error)
      toast({
        title: "Error",
        description: error.message || "Failed to download quote",
        variant: "destructive",
      })
    }
  }

  // Handle update quote status
  const handleUpdateQuoteStatus = async (quoteId: string, status: string) => {
    try {
      if (status === 'paid') {
        await markAsPaid(parseInt(quoteId))
      } else {
        await invoicesApi.markAsUnpaid(quoteId)
      }
      toast({
        title: "Success",
        description: "Quote status updated successfully",
      })
      await loadQuotes()
    } catch (error) {
      console.error('Error updating quote status:', error)
      toast({
        title: "Error",
        description: "Failed to update quote status",
        variant: "destructive",
      })
    }
  }



  if (loading) {
    return <PageLoader />
  }

  if (!prospect) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, textAlign: "center" }}>
        <Box sx={{ mx: "auto", display: "flex", height: 64, width: 64, alignItems: "center", justifyContent: "center", borderRadius: "50%", bgcolor: (t) => alpha(t.palette.error.main, 0.1), mb: 2 }}>
          <Box sx={{ display: "flex", color: "error.main" }}><User size={32} /></Box>
        </Box>
        <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary", mb: 1 }}>
          Prospect not found
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 3, maxWidth: 384 }}>
          The prospect you're looking for doesn't exist or you don't have permission to view it.
        </Typography>
        <BackButton label="Back to Prospects" onClick={() => navigate(prospectListPathForLead(prospect))} />
      </Box>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header: back + record-level actions */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <BackButton onClick={() => navigate(prospectListPathForLead(prospect))} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<TrendingUp size={16} />}
            onClick={handleConvertToCustomer}
          >
            Convert to Customer
          </Button>
          <ShareMenu
            items={buildEntityShareItems({
              subject: prospectShareSubject(prospect),
              onShareToChat: () => setChatShareOpen(true),
              notify: (m) => toast(m),
            })}
          />
          <Button variant="outlined" color="inherit" size="small" startIcon={<MoreHorizontal size={16} />} onClick={(e) => setActionsAnchor(e.currentTarget)}>
            Actions
          </Button>
          <Menu
            anchorEl={actionsAnchor}
            open={Boolean(actionsAnchor)}
            onClose={() => setActionsAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Prospect Actions</ListSubheader>
            <MenuItem onClick={() => { setActionsAnchor(null); handleEditProspect(); }}>
              <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
              Edit Prospect
            </MenuItem>
            <Divider />
            <MenuItem
              sx={{ color: "error.main" }}
              onClick={() => { setActionsAnchor(null); handleDeleteProspect(); }}
            >
              <Box sx={{ mr: 1, display: "flex" }}><X size={16} /></Box>
              Delete Prospect
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Identity */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar
          src={`https://api.dicebear.com/7.x/initials/svg?seed=${prospect.company_name || prospect.full_name}`}
          sx={{ width: 56, height: 56 }}
        >
          {prospect.company_name
            ? prospect.company_name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase()
            : `${prospect.first_name?.[0] || 'P'}${prospect.last_name?.[0] || 'R'}`}
        </Avatar>
        <Box>
          <Typography component="h2" sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>{prospect.company_name || 'Unknown Company'}</Typography>
          <Box component="span" sx={{ color: "text.secondary", fontSize: "0.875rem", bgcolor: "action.hover", px: 1, py: 0.5, borderRadius: 1, display: "inline-block", mt: 0.5 }}>{prospect.customer_id || prospect.id}</Box>
        </Box>
      </Box>

      {/* Section tabs. AI-created prospects get the "AI Lead" ACTION tab
          last — it triggers handleAiLead instead of switching content. */}
      <DetailTabs
        tabs={isAiProspect(prospect) ? [...visibleProspectTabs, AI_LEAD_TAB] : visibleProspectTabs}
        activeId={activeTab}
        onTabChange={(id) => {
          if (id === AI_LEAD_TAB.id) {
            if (!aiLeadResolving) void handleAiLead()
            return
          }
          setActiveTab(id)
        }}
        ariaLabel="Prospect sections"
      />

      {/* Active tab content */}
      {activeTab === 'overview' && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, alignItems: "start" }}>
          <ProspectOverviewTab
            prospect={prospect}
            editable={canManage}
            onAssignSalespeople={handleInlineAssignSalespeople}
            onChangePriority={handleInlineChangePriority}
            onChangeIndustry={handleInlineChangeIndustry}
            onChangeReseller={handleInlineAssignReseller}
            resellerOptions={resellerOptions}
          />
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Contact size={20} />
                  <Typography component="span" sx={{ fontWeight: 600 }}>Contacts</Typography>
                </Box>
              }
            />
            <CardContent>
              <EditableContactInformationPanel
                data={prospect}
                customerId={prospect.id}
                onContactsUpdated={(contacts) => {
                  setProspect(prev => prev ? { ...prev, customer_contacts: contacts } : null)
                }}
              />
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 'activity' && (
        <ActivityLogPanel
          customerId={prospectId}
          customerName={prospect.full_name}
          showScheduleButton={true}
          autoRefresh={true}
        />
      )}

      {activeTab === 'quotes' && hasFinancialAccess && (
        <DocumentList
          documents={quotes}
          documentType="quote"
          customerId={prospectId}
          onDeleteDocument={handleDeleteQuote}
          onDownloadFile={handleDownloadQuote}
          onUpdateStatus={handleUpdateQuoteStatus}
          onRefresh={loadQuotes}
          showNewButton={true}
          formatCurrency={formatMoney}
        />
      )}

      {activeTab === 'notes' && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, alignItems: "start" }}>
          <NotesPanel
            customerId={prospect.id}
            currentUserName="Current User"
          />
          <FileManagerPanel
            customerId={prospect.id}
          />
        </Box>
      )}

      {/* Edit Prospect Modal */}
      <ProspectFormModal
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        prospect={prospect}
        onSuccess={handleProspectUpdated}
      />

      {/* Delete Prospect Modal */}
      <DeleteProspectModal
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        prospect={prospect}
        onNavigateToList={handleNavigateToList}
      />

      {/* Share this prospect into the internal staff chat */}
      <ShareToChatDialog
        open={chatShareOpen}
        onClose={() => setChatShareOpen(false)}
        subject={prospectShareSubject(prospect)}
      />

      {/* Read-only AI-lead brief (non-super-admin path of the "AI Lead"
          action — super_admins navigate to /sales/leads/:id instead) */}
      <AiLeadBriefModal
        open={aiLeadModalOpen}
        onClose={() => setAiLeadModalOpen(false)}
        prospectId={prospectId}
        prospectName={prospect.company_name || prospect.full_name || undefined}
      />
    </Box>
  )
}

export default withProspectsAccess(ProspectDetailsPage)
